import { useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValueEvent } from "framer-motion";
import SplitHeading from "../ui/SplitHeading";
import useScrollVelocitySkew from "../../hooks/useScrollVelocitySkew";
import aptacyScreen from "../../assets/aptacy-screenshot.jpg";
import balansScreen from "../../assets/balans-screenshot.jpg";
import quickhomeScreen from "../../assets/quickhome-screenshot.jpg";
import quickhubScreen from "../../assets/quickhub-screenshot.jpg";
import majowkowyScreen from "../../assets/majowkowy-balans-screenshot.jpg";
import bottomlineScreen from "../../assets/bottomline-screenshot.jpg";
import gustiScreen from "../../assets/gusti-screenshot.jpg";
import gustiKatalogScreen from "../../assets/gusti-katalog-screenshot.jpg";
import motylekScreen from "../../assets/kolorowymotylek-screenshot.jpg";
import PortfolioPreview from "../ui/PortfolioPreview";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";
import SectionSubtitle from "../ui/SectionSubtitle";

// Przewijanie poziome desktop: tor rusza po TRACK_START przewinięcia sekcji (nagłówek jedzie
// jeszcze pionowo), a na 1 px przewinięcia przypada TRACK_PX_PER_SCROLL px toru. Wysokość
// sekcji wynika z szerokości toru, więc dodanie lub ukrycie karty nie wymaga dobierania jej ręcznie.
const TRACK_START = 0.12;
const TRACK_PX_PER_SCROLL = 1.2;

function PortfolioCardLinks({ card, hasLink, lang, invert = false }) {
  const ice = invert ? "#7A6244" : "#7CC4D8";
  const amber = invert ? "#7A6244" : "#C87941";

  return (
    <div className="relative z-10 flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 shrink-0">
      {card.slug && (
        <Link
          to={`/realizacje/${card.slug}`}
          className="link-hover-line font-body text-[11px] tracking-[0.2em] uppercase inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-90"
          style={{ color: ice }}
          onClick={(e) => e.stopPropagation()}
        >
          Case study →
        </Link>
      )}
      {hasLink ? (
        <a
          href={card.link}
          target="_blank"
          rel="noreferrer"
          className="link-hover-line font-body text-[11px] tracking-[0.2em] uppercase inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-90"
          style={{ color: amber }}
          onClick={(e) => e.stopPropagation()}
        >
          {card.linkLabel} ↗
        </a>
      ) : (
        <span
          className="font-mono text-[9px] tracking-[0.22em] uppercase inline-flex items-center gap-1.5 px-3 py-1"
          style={{ color: "#7CC4D8", border: "1px solid rgba(124,196,216,0.28)", borderRadius: 99, background: "rgba(124,196,216,0.07)" }}
        >
          {lang === "pl" ? "Wkrótce" : "Coming soon"}
        </span>
      )}
    </div>
  );
}

// Kolejność ustalona ręcznie przez właściciela (2026-10-06).
// `id` łączy kartę z tekstami w content.js; `slug` z case study (brak = brak podstrony).
const CARDS_STATIC = [
  {
    id: "quick-home",
    slug: "quick-home",
    title: "Quick Home",
    tech: ["React", "Tailwind CSS", "Netlify Forms", "Netlify"],
    link: "https://www.quick-home.pl/",
    linkLabel: "quick-home.pl",
    accent: "#5B8DB8",
    bg: "linear-gradient(140deg, #14110c 0%, #241c12 100%)",
    Preview: () => <PortfolioPreview src={quickhomeScreen} alt="Quick Home — domy modułowe i QuickCabin" />,
  },
  {
    id: "szkolenia-balans",
    slug: "szkolenia-balans",
    title: "Szkolenia Balans",
    tech: ["React 19", "Framer Motion", "Canvas API", "Lenis", "FastAPI", "MongoDB", "Netlify"],
    link: "https://szkoleniabalans.pl",
    linkLabel: "szkoleniabalans.pl",
    accent: "#E8607A",
    bg: "linear-gradient(140deg, #0c0815 0%, #1a0e1c 100%)",
    Preview: () => <PortfolioPreview src={balansScreen} alt="Szkolenia Balans" />,
  },
  {
    id: "gusti-katalog-prezentow",
    slug: "gusti-katalog-prezentow",
    title: "Gusti D'Italia · Katalog prezentów",
    tech: ["JavaScript", "page-flip", "Cloudflare Workers"],
    link: "https://gusti-katalogi.przestrzenapps.workers.dev/katalog/",
    linkLabel: "Otwórz katalog",
    accent: "#D98A55",
    bg: "linear-gradient(140deg, #1c1814 0%, #2a2219 100%)",
    Preview: () => <PortfolioPreview src={gustiKatalogScreen} alt="Gusti D'Italia — interaktywny katalog prezentów Collezione Regali 2026" />,
  },
  {
    id: "gusti-di-italia",
    slug: "gusti-di-italia",
    title: "Gusti di Italia",
    tech: ["Shoper", "Własny motyw", "CSS", "JavaScript"],
    link: "https://gustiditalia.pl/",
    linkLabel: "gustiditalia.pl",
    accent: "#C9A45C",
    bg: "linear-gradient(140deg, #0b0907 0%, #1a140c 100%)",
    Preview: () => <PortfolioPreview src={gustiScreen} alt="Gusti di Italia — sklep z włoskimi delikatesami" />,
  },
  {
    id: "bottomline",
    slug: "bottomline",
    title: "Bottom Line · Biuro rachunkowe",
    tech: ["React", "Tailwind CSS", "Netlify Forms", "Netlify"],
    link: "https://bottomline.pl/",
    linkLabel: "bottomline.pl",
    accent: "#2E4C3E",
    bg: "linear-gradient(140deg, #0c1f18 0%, #16332a 100%)",
    Preview: () => <PortfolioPreview src={bottomlineScreen} alt="Bottom Line — Biuro rachunkowe" />,
  },
  {
    id: "quickhub-terminal",
    slug: "quickhub-terminal",
    title: "QuickHub Terminal",
    tech: ["React", "Tailwind CSS", "Netlify Forms", "Netlify"],
    link: "https://quickhubterminal.pl/",
    linkLabel: "quickhubterminal.pl",
    accent: "#DD6B20",
    bg: "linear-gradient(140deg, #120e0a 0%, #241408 100%)",
    Preview: () => <PortfolioPreview src={quickhubScreen} alt="QuickHub Terminal — kontenery morskie" />,
  },
  {
    id: "kolorowy-motylek",
    slug: "kolorowy-motylek",
    title: "Kolorowy Motylek",
    tech: ["WordPress", "WooCommerce", "Własny motyw", "Complianz"],
    link: "https://kolorowymotylek.pl/",
    linkLabel: "kolorowymotylek.pl",
    accent: "#7B5CFF",
    bg: "linear-gradient(140deg, #fbf9f4 0%, #efe9ff 100%)",
    Preview: () => <PortfolioPreview src={motylekScreen} alt="Kolorowy Motylek — sklep szkolno-papierniczy i z zabawkami" />,
  },
  {
    id: "aptacy-nieruchomosci",
    slug: "aptacy-nieruchomosci",
    title: "Aptacy Nieruchomości",
    tech: ["React", "Tailwind CSS", "Leaflet.js", "Netlify Forms", "Netlify"],
    link: "https://www.aptacy-nieruchomosci.pl/",
    linkLabel: "aptacy-nieruchomosci.pl",
    accent: "#C7A35A",
    bg: "linear-gradient(140deg, #0a1a0d 0%, #132b19 100%)",
    Preview: () => <PortfolioPreview src={aptacyScreen} alt="Aptacy Nieruchomości" />,
  },
  {
    id: "przestrzen-control",
    slug: null,
    title: "Przestrzeń Control",
    tech: ["React", "FastAPI", "MongoDB", "Netlify"],
    link: "#",
    linkLabel: "przestrzen.control",
    accent: "#1C64F2",
    bg: "linear-gradient(140deg, #0a0e1f 0%, #0f1530 100%)",
    Preview: () => (
      <div className="absolute inset-0 overflow-hidden" style={{ background: "linear-gradient(140deg, #0a0e1f 0%, #0f1530 100%)" }}>
        {/* Browser chrome */}
        <div style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "9px 14px", display: "flex", alignItems: "center", gap: 7 }}>
          {[0.35, 0.2, 0.2].map((o, i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: `rgba(255,255,255,${o})` }} />
          ))}
          <div style={{ flex: 1, height: 18, borderRadius: 4, background: "rgba(255,255,255,0.05)", marginLeft: 8, display: "flex", alignItems: "center", paddingLeft: 10 }}>
            <span style={{ fontSize: 8, color: "rgba(255,255,255,0.22)", fontFamily: "monospace", letterSpacing: "0.04em" }}>przestrzen.control</span>
          </div>
        </div>
        {/* Layout */}
        <div style={{ display: "flex", height: "calc(100% - 38px)" }}>
          {/* Sidebar */}
          <div style={{ width: 130, borderRight: "1px solid rgba(255,255,255,0.05)", padding: "14px 0", flexShrink: 0, background: "rgba(0,0,0,0.18)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 12px 14px" }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, background: "linear-gradient(135deg, #1C64F2, #3B82F6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "white" }}>P</span>
              </div>
              <div>
                <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.80)", fontWeight: 600, lineHeight: 1.2 }}>Przestrzeń</div>
                <div style={{ fontSize: 6.5, color: "rgba(255,255,255,0.28)", letterSpacing: "0.18em" }}>CONTROL</div>
              </div>
            </div>
            {[
              { label: "Dashboard",    active: false },
              { label: "Wydarzenia",   active: true  },
              { label: "Zespół",       active: false },
              { label: "Harmonogram",  active: false },
              { label: "Zadania",      active: false },
              { label: "Raporty",      active: false },
            ].map((item) => (
              <div key={item.label} style={{ padding: "6px 12px", display: "flex", alignItems: "center", gap: 7, background: item.active ? "rgba(28,100,242,0.13)" : "transparent", borderRight: item.active ? "2px solid #1C64F2" : "2px solid transparent" }}>
                <div style={{ width: 13, height: 13, borderRadius: 3, background: item.active ? "rgba(28,100,242,0.35)" : "rgba(255,255,255,0.07)", flexShrink: 0 }} />
                <span style={{ fontSize: 8, color: item.active ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.28)", fontFamily: "sans-serif" }}>{item.label}</span>
              </div>
            ))}
          </div>
          {/* Main */}
          <div style={{ flex: 1, padding: "14px 16px", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", fontWeight: 600, fontFamily: "sans-serif" }}>Wydarzenia</div>
                <div style={{ fontSize: 8, color: "rgba(255,255,255,0.25)", marginTop: 1, fontFamily: "sans-serif" }}>Zarządzaj wydarzeniami i festiwalami</div>
              </div>
              <div style={{ padding: "5px 10px", borderRadius: 6, background: "#1C64F2", fontSize: 8.5, color: "white", fontWeight: 600, fontFamily: "sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                <span>+</span><span>Nowe wydarzenie</span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { name: "Majówkowy Balans", date: "1–4 maja", loc: "Mazury", status: "Nadchodzące", sc: "#F59E0B", persons: 24 },
                { name: "Letni Balans",     date: "15–18 lipca", loc: "Bieszczady", status: "Szkic",       sc: "rgba(255,255,255,0.3)", persons: 0  },
                { name: "Jesienny Balans",  date: "20–22 września", loc: "Mazury", status: "Zakończone",  sc: "#10B981", persons: 32 },
              ].map((ev) => (
                <div key={ev.name} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 7, padding: "9px 11px", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 4, marginBottom: 4 }}>
                    <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.72)", fontWeight: 600, fontFamily: "sans-serif", lineHeight: 1.3 }}>{ev.name}</div>
                    <span style={{ fontSize: 6.5, padding: "2px 5px", borderRadius: 99, background: `${ev.sc}22`, color: ev.sc, whiteSpace: "nowrap", flexShrink: 0, fontFamily: "sans-serif" }}>{ev.status}</span>
                  </div>
                  <div style={{ fontSize: 7.5, color: "rgba(255,255,255,0.24)", fontFamily: "sans-serif" }}>{ev.date} · {ev.loc}</div>
                  <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: "rgba(28,100,242,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 7, color: "#3B82F6" }}>✦</span>
                    </div>
                    <div style={{ height: 5, width: 60, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min((ev.persons / 36) * 100, 100)}%`, background: "linear-gradient(to right, #1C64F2, #3B82F6)", borderRadius: 99 }} />
                    </div>
                    <span style={{ fontSize: 7.5, color: "rgba(255,255,255,0.28)", fontFamily: "sans-serif" }}>{ev.persons} os.</span>
                  </div>
                </div>
              ))}
              <div style={{ border: "1px dashed rgba(255,255,255,0.07)", borderRadius: 7, padding: "9px 11px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.09)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "rgba(255,255,255,0.18)", lineHeight: 1 }}>+</div>
                <span style={{ fontSize: 7.5, color: "rgba(255,255,255,0.18)", fontFamily: "sans-serif" }}>Dodaj wydarzenie</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    // Wstrzymane: aplikacja zdjęta ze Sklepu Play do naprawy. Usuń `hidden` (i `hidden` w
    // caseStudies.js), gdy wróci — karta i case study pojawią się z powrotem.
    id: "majowkowy-balans",
    slug: "majowkowy-balans",
    hidden: true,
    title: "Majówkowy Balans",
    tech: ["React PWA", "Service Worker", "Framer Motion", "FastAPI", "Google Play"],
    link: "https://play.google.com/store/apps/details?id=pl.miloszczarnecki.balansapp&hl=pl",
    linkLabel: "Google Play",
    accent: "#78BE44",
    bg: "#0a0a0a",
    Preview: () => <PortfolioPreview src={majowkowyScreen} alt="Majówkowy Balans — aplikacja mobilna" />,
  },
];

export default function Portfolio() {
  const { lang } = useLang();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const travelRef = useRef(0);
  const [scrollSpan, setScrollSpan] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [activeCard, setActiveCard] = useState(0);
  const [tilt, setTilt] = useState({ id: null, rx: 0, ry: 0, ox: 50, oy: 50 });

  const CARDS = CARDS_STATIC.filter((s) => !s.hidden).map((s) => ({
    ...s,
    tag:  c.portfolio.cards[s.id].tag[lang],
    desc: c.portfolio.cards[s.id].desc[lang],
  }));

  // Ile toru wystaje poza ekran = dystans do przejechania; z niego wynika długość przewijania sekcji.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const measure = () => {
      const travel = Math.max(0, track.scrollWidth - window.innerWidth);
      travelRef.current = travel;
      setScrollSpan(travel / TRACK_PX_PER_SCROLL / (1 - TRACK_START));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const handleCardMove = (e, cardId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x  = e.clientX - rect.left;
    const y  = e.clientY - rect.top;
    const rx = ((y - rect.height / 2) / rect.height) * -10;
    const ry = ((x - rect.width  / 2) / rect.width)  *  12;
    const ox = (x / rect.width)  * 100;
    const oy = (y / rect.height) * 100;
    setTilt({ id: cardId, rx, ry, ox, oy });
  };

  const handleCardLeave = () => {
    setTilt({ id: null, rx: 0, ry: 0, ox: 50, oy: 50 });
    setHoveredId(null);
  };

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, (p) => {
    const t = Math.min(Math.max((p - TRACK_START) / (1 - TRACK_START), 0), 1);
    return -t * travelRef.current;
  });
  const smoothX     = useSpring(x, { stiffness: 80, damping: 22, mass: 0.6 });
  const progressScale = useTransform(scrollYProgress, [TRACK_START, 1], [0, 1]);
  const hintOpacity   = useTransform(scrollYProgress, [0, 0.13], [1, 0]);
  const skewX         = useScrollVelocitySkew(5);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const total = CARDS.length + 1;
    setActiveCard(Math.min(Math.floor(v * total), total - 1));
  });

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className="relative bg-navy-deep lg:h-[var(--portfolio-h)]"
      style={{
        position: "relative",
        overflowX: "clip",
        "--portfolio-h": scrollSpan ? `calc(100vh + ${Math.round(scrollSpan)}px)` : "600vh",
      }}
      data-testid="portfolio-section"
    >
      {/* ── Header ── */}
      <div className="px-6 lg:px-20 pt-20 pb-12 relative" style={{ background: "#100F0C", zIndex: 20 }}>
        <span
          className="font-body text-[11px] uppercase"
          style={{ color: "#7CC4D8", letterSpacing: "0.2em" }}
          data-testid="section-label"
        >
          {c.portfolio.label[lang]}
        </span>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mt-4">
          <div>
            <SplitHeading
              line1={c.portfolio.heading1[lang]}
              line2={<em className="italic text-amber">{c.portfolio.ice[lang]}</em>}
              className="font-display font-light leading-[1.02] text-white-warm"
              style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)" }}
              data-testid="portfolio-title"
            />
            <div style={{ width: 72, height: 1, background: "rgba(200,121,65,0.4)", marginTop: 14 }} />
          </div>
          <SectionSubtitle className="text-white-dim max-w-xs lg:text-right">
            {c.portfolio.subtitle[lang]}
          </SectionSubtitle>
        </div>
      </div>

      {/* ── Mobile — vertical stack ── */}
      <div className="lg:hidden px-6 pb-16 flex flex-col gap-6">
        {CARDS.map((card) => {
          const Preview = card.Preview;
          const hasLink = card.link && card.link !== "#";
          return (
            <article
              key={card.id}
              className="relative overflow-hidden"
              style={{
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.08)",
                background: card.invert ? card.bg : "rgba(255,255,255,0.04)",
                cursor: hasLink ? "pointer" : "default",
              }}
              onClick={() => hasLink && window.open(card.link, "_blank", "noreferrer")}
            >
              <div style={{ height: 300, background: card.bg, overflow: "hidden", position: "relative" }}>
                <Preview />
              </div>
              <div
                className="p-6"
                style={{
                  background: card.invert
                    ? "#F4EFE8"
                    : "linear-gradient(180deg, rgba(16,15,12,0.7), rgba(16,15,12,0.95))",
                }}
              >
                <p
                  className="font-body text-[10px] tracking-[0.28em] uppercase mb-2"
                  style={{ color: card.invert ? "#7A6244" : "rgba(200,121,65,0.85)" }}
                >
                  {card.tag}
                </p>
                <h3
                  className="font-display font-light"
                  style={{ fontSize: "1.5rem", color: card.invert ? "#2A2018" : "#F2E8D8" }}
                >
                  {card.title}
                </h3>
                <p
                  className="font-body text-sm mt-2 leading-[1.7]"
                  style={{ color: card.invert ? "rgba(42,32,24,0.7)" : "rgba(242,232,216,0.6)" }}
                >
                  {card.desc}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {card.tech.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[10px] tracking-wide px-2.5 py-1 rounded-md border"
                      style={{
                        color: card.invert ? "#7A6244" : "rgba(200,121,65,0.9)",
                        background: card.invert ? "rgba(122,98,68,0.08)" : "rgba(200,121,65,0.06)",
                        borderColor: card.invert ? "rgba(122,98,68,0.25)" : "rgba(200,121,65,0.18)",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <PortfolioCardLinks card={card} hasLink={hasLink} lang={lang} invert={card.invert} />
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Sticky wrapper — desktop ── */}
      <div className="hidden lg:flex sticky top-0 h-screen overflow-hidden items-center">
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-glass-border z-30" aria-hidden="true">
          <motion.div
            className="origin-left h-full"
            style={{ scaleX: progressScale, background: "linear-gradient(to right, #7CC4D8, rgba(124,196,216,0.4))" }}
          />
        </div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-9 left-6 lg:left-20 z-30 pointer-events-none flex items-center gap-2"
          style={{ opacity: hintOpacity }}
          aria-hidden="true"
        >
          <motion.span
            animate={{ x: [0, 7, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="font-mono text-[10px] tracking-[0.38em] uppercase"
            style={{ color: "#7CC4D8" }}
          >
            scroll →
          </motion.span>
        </motion.div>

        {/* Dot progress indicators */}
        <div className="absolute bottom-9 right-6 lg:right-20 z-30 flex items-center gap-2" aria-hidden="true">
          {Array.from({ length: CARDS.length + 1 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 5,
                width: activeCard === i ? 20 : 5,
                borderRadius: 999,
                background: activeCard === i ? "#7CC4D8" : "rgba(255,255,255,0.16)",
                transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            />
          ))}
        </div>

        {/* Horizontal track */}
        <motion.div ref={trackRef} className="flex gap-8 pl-6 lg:pl-20 pr-20 py-12" style={{ x: smoothX, skewX }}>
          {CARDS.map((card, idx) => {
            const Preview = card.Preview;
            const hasLink = card.link && card.link !== "#";
            return (
              <article
                key={card.id}
                data-cursor="view"
                data-testid={`portfolio-card-${card.id}`}
                className="relative flex-shrink-0"
                style={{
                  width: "min(78vw, 880px)",
                  height: "70vh",
                  maxHeight: "70vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  background: card.invert ? card.bg : "rgba(255,255,255,0.04)",
                  backdropFilter: card.invert ? "none" : "blur(14px)",
                  borderRadius: 12,
                  border: hoveredId === card.id
                    ? "1px solid rgba(124,196,216,0.45)"
                    : "1px solid rgba(255,255,255,0.08)",
                  boxShadow: hoveredId === card.id
                    ? "0 0 50px rgba(200,121,65,0.10), inset 0 0 40px rgba(200,121,65,0.05)"
                    : "none",
                  transform: tilt.id === card.id
                    ? `perspective(1200px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) translateZ(8px)`
                    : "perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0px)",
                  transition: tilt.id === card.id
                    ? "border-color 0.35s ease, box-shadow 0.35s ease"
                    : "all 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
                  cursor: hasLink ? "pointer" : "default",
                  willChange: "transform",
                }}
                onMouseEnter={() => setHoveredId(card.id)}
                onMouseMove={(e) => handleCardMove(e, card.id)}
                onMouseLeave={handleCardLeave}
                onClick={() => hasLink && window.open(card.link, "_blank", "noreferrer")}
              >
                {tilt.id === card.id && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute", inset: 0, zIndex: 25, pointerEvents: "none",
                      borderRadius: "inherit",
                      background: `radial-gradient(circle at ${tilt.ox}% ${tilt.oy}%, rgba(255,255,255,${card.invert ? "0.18" : "0.10"}) 0%, rgba(255,255,255,0) 55%)`,
                    }}
                  />
                )}

                <div className="absolute top-5 left-5 z-20 font-mono text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: card.invert ? "#7A6244" : "rgba(200,121,65,0.7)" }}>
                  {String(idx + 1).padStart(2, "0")} / {String(CARDS.length).padStart(2, "0")}
                </div>

                <div
                  className="relative w-full"
                  style={{
                    height: "42%",
                    flexShrink: 0,
                    background: card.bg,
                    borderRadius: "8px 8px 0 0",
                    overflow: "hidden",
                    filter: hoveredId === card.id ? "brightness(1.05)" : "brightness(1)",
                    transition: "filter 0.4s ease",
                  }}
                >
                  <Preview />
                </div>

                <div
                  className="relative p-7 lg:p-8 flex flex-col"
                  style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    background: card.invert
                      ? "#F4EFE8"
                      : "linear-gradient(180deg, rgba(16,15,12,0.7), rgba(16,15,12,0.95))",
                    color: card.invert ? "#2A2018" : undefined,
                  }}
                >
                  <div>
                    <p
                      className="font-body text-[10px] tracking-[0.28em] uppercase mb-3"
                      style={{ color: card.invert ? "#7A6244" : "rgba(200,121,65,0.85)" }}
                    >
                      {card.tag}
                    </p>
                    <h3
                      className="font-display font-light leading-[1.05]"
                      style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)", color: card.invert ? "#2A2018" : "#F2E8D8" }}
                    >
                      {card.title}
                    </h3>
                    <p
                      className="font-body text-sm mt-2 max-w-md leading-[1.7]"
                      style={{ color: card.invert ? "rgba(42,32,24,0.7)" : "rgba(242,232,216,0.6)" }}
                    >
                      {card.desc}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {card.tech.map((t) => (
                      <span
                        key={t}
                        className="font-mono text-[10px] tracking-wide px-2.5 py-1 rounded-md border"
                        style={{
                          color: card.invert ? "#7A6244" : "rgba(200,121,65,0.9)",
                          background: card.invert ? "rgba(122,98,68,0.08)" : "rgba(200,121,65,0.06)",
                          borderColor: card.invert ? "rgba(122,98,68,0.25)" : "rgba(200,121,65,0.18)",
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <PortfolioCardLinks card={card} hasLink={hasLink} lang={lang} invert={card.invert} />
                </div>
              </article>
            );
          })}

          {/* End CTA card */}
          <article
            className="flex-shrink-0 flex flex-col items-center justify-center text-center gap-6 px-10"
            style={{
              width: "min(50vw, 480px)", height: "70vh",
              background: "rgba(255,255,255,0.04)", borderRadius: 12,
              border: "1px solid rgba(200,121,65,0.25)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }}
          >
            <div
              className="flex items-center justify-center text-amber"
              style={{ width: 64, height: 64, borderRadius: "50%", border: "1px solid rgba(200,121,65,0.3)", fontSize: "2rem", lineHeight: 1 }}
            >
              +
            </div>
            <h3 className="font-display text-2xl font-light text-white-warm">
              {c.portfolio.cta_title[lang]}
            </h3>
            <p className="font-body text-sm max-w-[12rem] leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
              {c.portfolio.cta_desc[lang]}
            </p>
            <a
              href="#kontakt"
              className="font-body text-sm px-6 py-2 rounded-full"
              style={{ border: "1px solid #7CC4D8", color: "#7CC4D8", fontWeight: 600, background: "transparent", transition: "all 0.3s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#7CC4D8"; e.currentTarget.style.color = "#100F0C"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#7CC4D8"; }}
            >
              {c.portfolio.cta_btn[lang]}
            </a>
          </article>
        </motion.div>
      </div>
    </section>
  );
}
